import React from 'react';
import { useNavigate } from 'react-router-dom';

const SUPPORT_EMAIL = 'support@flashtickets.fm';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="text-xl font-semibold text-white mb-4">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Item({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <details className="group rounded-xl border border-white/[0.06] bg-white/[0.02] px-5 py-4">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-medium text-white/90 [&::-webkit-details-marker]:hidden">
        {q}
        <svg
          className="h-4 w-4 flex-shrink-0 text-white/40 transition-transform group-open:rotate-180"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </summary>
      <div className="mt-3 space-y-2 text-sm leading-relaxed text-white/60">{children}</div>
    </details>
  );
}

export function HelpPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white/90 font-sans px-6 py-12">
      <div className="max-w-3xl mx-auto">
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
          className="text-sm text-[#3b82f6] hover:text-[#3b82f6]/80 transition-colors"
        >
          &larr; Back
        </button>

        <h1 className="text-3xl font-bold mt-6 mb-3 text-white">Help &amp; Support</h1>
        <p className="text-white/60 mb-10">
          Answers to the most common questions. Can&apos;t find yours? Email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#3b82f6] hover:text-[#3b82f6]/80">
            {SUPPORT_EMAIL}
          </a>{' '}
          and we&apos;ll get back to you.
        </p>

        <Section title="Getting started">
          <Item q="How do I connect my ticket accounts?">
            <p>
              Go to <strong className="text-white/80">Connections</strong> and pick your platform —
              DICE, AXS, or TAO. DICE signs in with your phone number and a one-time code (sent by
              SMS or email). AXS and TAO sign in with email and password; AXS also sends a code to
              the phone on your account.
            </p>
            <p>
              You can connect as many accounts per platform as you like — each one syncs its own
              tickets.
            </p>
          </Item>
          <Item q="Where are my tickets?">
            <p>
              <strong className="text-white/80">My Tickets</strong> shows every ticket from every
              connected account, grouped by event. Tickets appear after an account&apos;s first
              sync, which starts automatically when you connect. If something is missing, sync the
              account manually (see below).
            </p>
          </Item>
        </Section>

        <Section title="Accounts &amp; syncing">
          <Item q="A ticket I bought isn't showing — how do I resync?">
            <p>
              Open <strong className="text-white/80">Connections</strong> and use{' '}
              <strong className="text-white/80">Sync tickets</strong> on the account the purchase
              belongs to, or <strong className="text-white/80">Sync all</strong> at the top of the
              page. Each card shows when it last synced and whether the sync succeeded.
            </p>
          </Item>
          <Item q='What does "needs to be refreshed — re-login to keep syncing" mean?'>
            <p>
              The platform (most often AXS) expired our session, so syncing stopped until you sign
              in again. Go to <strong className="text-white/80">Connections</strong>, find the
              account marked <strong className="text-white/80">Reconnect needed</strong>, and click{' '}
              <strong className="text-white/80">Reconnect</strong> — it&apos;s the same short login
              you did when connecting. Your tickets and history are untouched; only the session is
              renewed.
            </p>
          </Item>
          <Item q="An account says it's banned — what now?">
            <p>
              The ticket platform has restricted that account on their side, so we can&apos;t sync
              or transfer from it. Resolve it with the platform directly; once it works on their
              site or app again, reconnect it here. If you think the banner is wrong, email{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#3b82f6] hover:text-[#3b82f6]/80">
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </Item>
        </Section>

        <Section title="Transfers">
          <Item q="How do I send tickets to someone?">
            <p>
              From <strong className="text-white/80">My Tickets</strong>: use{' '}
              <strong className="text-white/80">Transfer</strong> on a single ticket, the{' '}
              <strong className="text-white/80">Transfer (N)</strong> button on an event to send
              several tickets from that event (even across accounts), or{' '}
              <strong className="text-white/80">Transfer Multiple</strong> to batch-send across
              events. DICE transfers go to an email or a phone number; AXS and TAO go to an email.
            </p>
          </Item>
          <Item q="How do I check whether a transfer went through?">
            <p>
              The <strong className="text-white/80">Transfers</strong> page is the audit log: every
              transfer with its status, recipient, quantity, and source account. You can search it
              by event name and expand any row to see each attempt, including the exact error when
              one fails.
            </p>
          </Item>
          <Item q='I got a "possible duplicate" warning — what does it mean?'>
            <p>
              We noticed a recent transfer to the same recipient for the same event, and pause so
              you don&apos;t send the same tickets twice. If it&apos;s intentional, continue with{' '}
              <strong className="text-white/80">Send anyway</strong>.
            </p>
          </Item>
        </Section>

        <Section title="Waitlists">
          <Item q="How do waitlist joins work?">
            <p>
              <strong className="text-white/80">Waitlists</strong> lists sold-out DICE events. Pick
              a ticket type with an open waitlist and join with all your DICE accounts at once, or
              hand-pick accounts and quantities. Joins run in the background — a progress banner
              shows each account as it completes, and you can cancel a job mid-run.
            </p>
            <p>
              Waitlist signups count toward your plan&apos;s included usage; you can see every join
              itemised on your Profile.
            </p>
          </Item>
        </Section>

        <Section title="Dice account numbers">
          <Item q="What is a Dice account number?">
            <p>
              A phone number we rent for you so you can run a separate DICE account. We create the
              DICE account on that number and forward every text it receives (including login
              codes) to your email. Manage them under{' '}
              <strong className="text-white/80">Dice Accounts</strong> — change the forwarding
              email, check recent SMS, or cancel; a cancelled number stays active until the end of
              the period you paid for.
            </p>
          </Item>
          <Item q="A number's setup didn't finish — what do I do?">
            <p>
              Open <strong className="text-white/80">Dice Accounts</strong> and use{' '}
              <strong className="text-white/80">Retry Setup</strong> on the affected number. If it
              still won&apos;t complete, email us — include the phone number shown on the card.
            </p>
          </Item>
        </Section>

        <Section title="Billing &amp; usage">
          <Item q="How do I check what I'm being charged for?">
            <p>
              Your <strong className="text-white/80">Profile</strong> shows the full picture: your
              plan and renewal date, usage meters against what your plan includes, and an itemised{' '}
              <strong className="text-white/80">Usage detail</strong> panel — every waitlist joined,
              every ticket we&apos;ve identified in your accounts this period, and each Dice
              number you rent. The upcoming-invoice preview
              shows exactly what the next charge will be, and past invoices are downloadable as
              PDFs.
            </p>
          </Item>
          <Item q="How do I change or cancel my plan?">
            <p>
              <strong className="text-white/80">Billing</strong> (Profile &rarr; Manage plan) shows
              all plans and handles upgrades, downgrades, and cancellation through the secure
              Stripe portal. Card details go straight to Stripe and never touch our servers. When
              you cancel, you keep access until the end of the period you&apos;ve paid for.
            </p>
          </Item>
        </Section>

        <Section title="Contact support">
          <div className="rounded-xl border border-[#3b82f6]/25 bg-[#3b82f6]/[0.06] px-5 py-4 text-sm leading-relaxed text-white/70">
            <p className="mb-2">
              Signed in? Use the{' '}
              <a
                href="/v2/app/support"
                className="font-medium text-[#3b82f6] hover:text-[#3b82f6]/80"
              >
                Support form
              </a>{' '}
              — it lets you pick the connection and event, and attaches your account details for
              us. Otherwise email{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="font-medium text-[#3b82f6] hover:text-[#3b82f6]/80"
              >
                {SUPPORT_EMAIL}
              </a>{' '}
              for anything not covered here — connection trouble, a transfer that looks wrong, a
              charge you don&apos;t recognise, or a feature question.
            </p>
            <p className="text-white/50">
              To help us fix it fast, include: the email you sign in with, which platform and
              account it concerns, the event name, and a screenshot of what you&apos;re seeing.
            </p>
          </div>
        </Section>
      </div>
    </div>
  );
}
